import { apiFetch } from "./client";

export type StatoAnalisi = "in_corso" | "pronta" | "errore";
export type TipoPrezzo = "fisso" | "variabile";

export interface PartitaAnalisi {
  descrizione: string;
  importo: string;
}

export interface OffertaAnalisi {
  id: string;
  nome: string;
  fornitore: string;
  tipo_prezzo: string | null;
  prezzo: string | null;
  quota_fissa_mese: string | null;
  spesa_annua: string | null;
  differenza: string | null;
  motivo: string | null;
}

export interface RiferimentoMercato {
  indice: "PUN" | "PSV";
  /** AAAA-MM */
  mese: string;
  valore: string;
}

/** «Spesa annua sostenuta» stampata dal venditore col suo periodo di un anno (riquadro «La bolletta», 19/10) */
export interface SpesaAnnua {
  euro: string;
  /** AAAA-MM o AAAA-MM-GG, come stampato */
  da: string;
  a: string;
}

export interface FornituraAnalisi {
  commodity: "luce" | "gas";
  oggi_paghi: string | null;
  consumo_annuo: string | null;
  consumo_annuo_fonte: string | null;
  tipo_prezzo: string | null;
  motivo: string | null;
  altre_partite: PartitaAnalisi[];
  dopo_totale: PartitaAnalisi[];
  periodo_da?: string | null;
  periodo_a?: string | null;
  riferimento_mercato?: RiferimentoMercato | null;
  offerte: OffertaAnalisi[];
  /** assente nelle analisi fatte prima del 19/10 */
  spesa_annua?: SpesaAnnua | null;
  /** quota fissa di vendita al mese su cui si fa il conto (19/10) */
  quota_fissa_mese?: string | null;
}

export interface RisultatoAnalisi {
  fonte: string;
  motivo: string | null;
  tipo_prezzo: string | null;
  nome_offerta: string | null;
  scartati: string[];
  forniture: FornituraAnalisi[];
  /** «Totale bolletta» stampato, di tutta la bolletta; assente nelle analisi fatte prima del 19/10 */
  totale_bolletta?: string | null;
}

/** Fase 2, voci da segnalare: null = non prevista per questo operatore. */
export type StatoDettaglio = "su_richiesta" | "in_corso" | "pronta" | "errore";
/** «V»: elementi della formula del prezzo senza il loro valore (21/10) */
export type Giudizio = "G" | "P" | "N" | "V" | "da_verificare" | "in_regola";

export interface Segnalazione {
  voce: string;
  giudizio: Giudizio;
  dicitura: string;
  /** come mostrarla: la dicitura stampata, o il nome della quota per le righe del riepilogo */
  nome?: string;
  valore: string | null;
  /** «kwh», «smc», «mese», «kw/mese» quando il valore è un prezzo; null = importo in € */
  unita?: string | null;
  riferimento: string | null;
  euro_in_piu: string | null;
  fonte: string | null;
  /** perché è «da verificare»: «periodo», «tipo_cliente», «iva_22», «valore_ufficiale» */
  motivo?: string | null;
  /** spiegazione breve scritta da Luna, per le voci segnalate */
  descrizione?: string | null;
  /** % di perdite di rete aggiunte dal controllo alla formula del prezzo */
  perdite?: string | null;
  /** € in più stimati sulla quota di kWh nelle ore di picco ricavata dalla formula */
  stimato?: boolean;
  /** quella quota, in % («5.5») */
  quota_picco?: string | null;
  /** «V»: i nomi degli elementi della formula stampati senza valore */
  mancanti?: string[];
  /** l'analisi dell'AI: il controllo che l'ha prodotta */
  controllo?: string;
  /** riga del gas: `motivo` è una o più frasi approvate unite da «; » (non un codice della luce) */
  parte?: "gas";
}

/** Una voce della vista estesa: `ufficiale` = valore regolato, «prezzo_libero», o null (nessun confronto). */
export interface VoceFamiglia {
  nome: string;
  prezzo: string | null;
  unita: string | null;
  importo: string | null;
  ufficiale: string | null;
  /** vuoto = voce non controllata, salvo `esito` */
  giudizi: Giudizio[];
  voce: string | null;
  /** partite senza un prezzo da confrontare */
  esito?: "non_regolata" | "non_verificabile";
  /** «conguaglio», o il motivo del «da verificare» */
  motivo?: string | null;
  /** € in più di una gonfiata */
  euro_in_piu?: string | null;
  /** € in bolletta di una voce che potrebbe non esserci o non dovrebbe esserci */
  euro?: string | null;
  /** spiegazione breve scritta da Luna */
  descrizione?: string | null;
  /** prezzo fatturato contro somma della formula: € che la formula non spiega */
  euro_non_spiegati?: string | null;
  /** % di perdite di rete aggiunte dal controllo alla formula del prezzo */
  perdite?: string | null;
  /** € in più stimati sulla quota di kWh nelle ore di picco ricavata dalla formula */
  stimato?: boolean;
  /** quella quota, in % («5.5») */
  quota_picco?: string | null;
  /** «V»: i nomi degli elementi della formula stampati senza valore */
  mancanti?: string[];
  /** riga del gas la cui «gonfiata» è passata all'operatore: al cliente niente euro in più */
  parte?: "gas";
}

export interface Famiglia {
  chiave: "vendita" | "rete" | "imposte" | "altre";
  /** famiglia del gas: nella dual segue quella della luce con la stessa chiave; i motivi delle voci sono frasi */
  parte?: "gas";
  nome: string;
  totale: string | null;
  voci: VoceFamiglia[];
}

export interface Conteggi {
  controllate: number;
  da_segnalare: number;
  G: number;
  P: number;
  N: number;
  euro_in_piu: string | null;
  /** il totale comprende € stimati */
  euro_stimato?: boolean;
  principale: Segnalazione | null;
}

/** Il prezzo dell'energia letto dalla formula (fase 2): solo valori stampati, mai sommati. */
export interface RigaPrezzo {
  /** «F1F2» = dalle 7 alle 23 dal lunedì al sabato; «F3» = notte, domenica e festivi */
  fasce: "tutte" | "F1F2" | "F3";
  valore: string;
}

export interface PrezzoLetto {
  /** l'indice del variabile; null = prezzo fisso */
  indice: "PUN" | "PSV" | null;
  unita: "kwh" | "smc" | null;
  righe: RigaPrezzo[];
  quota_fissa_mese: string | null;
}

/** Nota dell'AI sulla bolletta: un tema, i suoi € se ci sono, il perché. */
export interface NotaAnalisi {
  tema: string;
  euro?: string;
  perche: string;
}

export interface Dettaglio {
  /** finisce in «-ai» quando l'analisi è dell'AI */
  versione?: string;
  note?: NotaAnalisi[];
  fornitore?: string | null;
  motivo?: string | null;
  segnalazioni?: Segnalazione[];
  famiglie?: Famiglia[];
  conteggi?: Conteggi;
  /** dati non scritti in bolletta: «periodo», «tipo_cliente» */
  mancano?: string[];
  errore?: string;
  /** assente se niente è stato letto, e nelle analisi fatte prima del 19/10 */
  prezzo?: PrezzoLetto;
  /** dettaglio della sola parte gas */
  parte?: "gas";
  /** l'AI del gas non ha risposto: le voci del gas sono quelle dei controlli automatici */
  ai_errore_gas?: string | null;
  /** il segno che il QR manda al cliente al posto di `ai_errore_gas` (ruling 68) */
  gas_non_analizzato?: boolean;
  /** bolletta col gas, luce che il motore non analizza: il motivo (come `motivo`) */
  motivo_luce?: string | null;
  /** periodo stampato diverso da quello del riepilogo letto dalla fase 1 (AAAA-MM-GG) */
  periodo_stampato?: { da: string; a: string } | null;
}

export interface Analisi {
  stato: StatoAnalisi;
  risultato: RisultatoAnalisi | null;
  dettaglio_stato?: StatoDettaglio | null;
  dettaglio?: Dettaglio | null;
}

export interface AnalisiSessione extends Analisi {
  analisi_id: string;
  creato_il: string;
}

export interface BollettaPratica extends Analisi {
  cbolletta_id: string;
  analisi_id: string | null;
}

export interface AnalisiPratica {
  attiva: boolean;
  bollette: BollettaPratica[];
}

export function getAnalisi(id: string): Promise<Analisi> {
  return apiFetch<Analisi>(`/analisi-bolletta/${encodeURIComponent(id)}`);
}

export function getAnalisiSessione(): Promise<AnalisiSessione[]> {
  return apiFetch<AnalisiSessione[]>("/analisi-bolletta/sessione");
}

/** Avvia (o riavvia dopo un errore) la fase 2 di un'analisi già pronta. */
export function avviaDettaglio(id: string): Promise<{ dettaglio_stato: StatoDettaglio }> {
  return apiFetch<{ dettaglio_stato: StatoDettaglio }>(`/analisi-bolletta/${encodeURIComponent(id)}/dettaglio`,
    { method: "POST" });
}

export function getAnalisiPratica(oppId: string): Promise<AnalisiPratica> {
  return apiFetch<AnalisiPratica>(`/analisi-bolletta/pratica/${encodeURIComponent(oppId)}`);
}
